import axios from "axios";
import * as qs from "qs";

import { Token } from "./Token";
import { ITokenService } from "./ITokenService";
import { getApiUrl, getFormHeaders } from "../commUtil";

const SESSIONSTORAGEKEY_TOKEN = "moveit.token";
/** Number of seconds prior to token expiration within which we should refresh the token. */
const TOKEN_REFRESH_THRESHOLD_SECONDS = 60;

const tokenServicePool: { [baseUrl: string]: ITokenService } = {};

export class TokenService implements ITokenService {
    /**
     * Returns an ITokenService instance for the given base URL.
     * Uses an instance pool such that two calls with the
     * same base URL argument will return the same instance.
     */
    public static getInstance(baseUrl: string, language: string) {
        if (!tokenServicePool[baseUrl]) {
            tokenServicePool[baseUrl] = new TokenService(baseUrl, language);
        }

        return tokenServicePool[baseUrl];
    }

    /**
     * Clears all entries from the instance pool.
     */
    public static resetPool() {
        const poolEntries = Object.getOwnPropertyNames(tokenServicePool);
        for (const entry of poolEntries) {
            delete tokenServicePool[entry];
        }
    }

    private readonly baseUrl: string = "";
    private readonly language: string = "";
    private token: Token = null;
    private tokenPromise: Promise<Token> = null;
    private timerHandle: any = null;
    private refreshing: boolean = false;

    private constructor(baseUrl: string, language: string) {
        this.baseUrl = baseUrl;
        this.language = language;

        const tokenString = sessionStorage.getItem(SESSIONSTORAGEKEY_TOKEN);
        if (tokenString) {
            this.token = JSON.parse(tokenString) as Token;
            // Do not start token reset timer until a token is actually requested
            //if (isValidToken(this.token)) {
            //    this.setTimer(this.token.expirationTimestamp);
            //}
        }
    }

    public getToken(forceRefresh?: boolean): Promise<Token> {
        if ((forceRefresh && !this.refreshing) || !this.tokenPromise) {
            this.resetTokenPromise(forceRefresh);
        }

        return this.tokenPromise;
    }

    public revokeToken(): Promise<void> {
        const url = getApiUrl(this.baseUrl, "token/revoke");
        const data = { token: this.token.access_token };
        const formHeaders = getFormHeaders();
        return axios
            .post(url, qs.stringify(data), { headers: formHeaders })
            .then(() => this.clearToken());
    }

    public clearToken(): void {
        this.token = null;
        this.tokenPromise = null;
        sessionStorage.removeItem(SESSIONSTORAGEKEY_TOKEN);
    }

    private resetTokenPromise(forceRefresh: boolean): void {
        this.unsetTimer();
        this.tokenPromise = new Promise((resolve, reject) => {
            const doAcquire = (): void => {
                this.acquireToken().then(
                    newToken => resolve(newToken),
                    error => reject(error)
                );
            };

            const doRefresh = (): void => {
                this.refreshToken().then(
                    newToken => resolve(newToken),
                    () => doAcquire()
                );
            };

            if (isValidToken(this.token)) {
                if (forceRefresh || shouldRefreshToken(this.token)) {
                    doRefresh();
                } else {
                    // Set refresh timer if not already set
                    if (!this.timerHandle) {
                        this.setTimer(this.token.expirationTimestamp);
                    }
                    resolve(this.token);
                }
            } else {
                doAcquire();
            }
        });
    }

    private processToken(token: Token): Token {
        token.expirationTimestamp = calculateTokenExpirationTimestamp(token);
        sessionStorage.setItem(SESSIONSTORAGEKEY_TOKEN, JSON.stringify(token));
        this.setTimer(token.expirationTimestamp);
        this.token = token;
        this.refreshing = false; // reset refresh status
        return token;
    }

    private acquireToken(): Promise<Token> {
        const url = getApiUrl(this.baseUrl, "token");
        const data = {
            grant_type: "session",
            language: this.language,
        };

        const formHeaders = getFormHeaders();
        return axios
            .post(url, qs.stringify(data), { headers: formHeaders })
            .then(response => this.processToken(response.data as Token));
    }

    private refreshToken(): Promise<Token> {
        this.refreshing = true; // don't let extra refresh requests go through
        const url = getApiUrl(this.baseUrl, "token");
        const data = {
            grant_type: "refresh_token",
            refresh_token: this.token.refresh_token,
        };
        const formHeaders = getFormHeaders();
        return axios
            .post(url, qs.stringify(data), { headers: formHeaders })
            .then(response => this.processToken(response.data as Token));
    }

    /**
     * Sets a timer that refreshes the token just after the refresh threshold
     * has been reached based on the provided token expiration timestamp.
     */
    private setTimer(expirationTimestamp: number): void {
        const dueTime =
            expirationTimestamp - Date.now() - (TOKEN_REFRESH_THRESHOLD_SECONDS - 1) * 1000;
        const executeRefresh = () => this.resetTokenPromise(false);

        if (dueTime > 0) {
            this.timerHandle = setTimeout(executeRefresh, dueTime);
        } else {
            executeRefresh();
        }
    }

    private unsetTimer(): void {
        if (this.timerHandle !== null) {
            clearTimeout(this.timerHandle);
            this.timerHandle = null;
        }
    }
}

function isValidToken(token: Token): boolean {
    return !!token && !isExpiredToken(token);
}

function isExpiredToken(token: Token): boolean {
    return token.expirationTimestamp <= Date.now();
}

function shouldRefreshToken(token: Token): boolean {
    return token.expirationTimestamp - Date.now() < TOKEN_REFRESH_THRESHOLD_SECONDS * 1000;
}

function calculateTokenExpirationTimestamp(token: Token): number {
    return Date.now() + token.expires_in * 1000;
}
